import activitySummaryApi from "@/api/activity-summary";
import { dispatchCrossMeetingSummaryRegenerated } from "@/common/helpers/custom-events";
import { usePolling } from "@/common/composables/polling";
import { useSharedAiSummary } from "@/experiences/ai-summary/composables/shared-ai-summary";
import { defineStore } from "pinia";
import { ref } from "vue";
const POLL_INTERVAL_MS = 2000;
const POLL_MAX_ATTEMPTS = 150; // ~5 min ceiling at 2s
const POLL_INITIAL_DELAY_MS = 3000; // backoff before the first poll — the async job can't finish sooner
export const useActivitySummaryStore = (studyId, activityId) => {
    return defineStore(`activity-summary-${studyId}-${activityId}`, () => {
        const summaryContent = ref(undefined);
        useSharedAiSummary().registerSummaryContent(summaryContent);
        const pending = ref(true);
        const isGenerating = ref(false);
        let disposed = false;
        const isSummaryOutdated = ref(false);
        const newMeetingCount = ref(0);
        const includedMeetingCount = ref(0);
        const totalMeetingCount = ref(0);
        const applyResponse = (res) => {
            summaryContent.value = res.content ?? undefined;
            isSummaryOutdated.value = res.outdated;
            newMeetingCount.value = res.newMeetingCount;
            includedMeetingCount.value = res.includedMeetingCount;
            totalMeetingCount.value = res.totalMeetingCount;
        };
        const applyError = (e) => (summaryContent.value = e);
        const polling = usePolling(async () => {
            try {
                const res = await activitySummaryApi.fetchSummary(studyId, activityId);
                // The backend clears the summary at the start of generation, so any content present
                // is the freshly generated result. Until then GET returns null and we keep polling.
                if (res.content != null) {
                    applyResponse(res);
                    if (isGenerating.value)
                        dispatchCrossMeetingSummaryRegenerated({ activityId });
                    return true;
                }
                return false;
            }
            catch (e) {
                // A persisted generation failure surfaces as an HTTP error the api client
                // wraps into a SummaryError.
                applyError(e);
                return true;
            }
        }, {
            intervalMs: POLL_INTERVAL_MS,
            maxAttempts: POLL_MAX_ATTEMPTS,
            initialDelayMs: POLL_INITIAL_DELAY_MS,
            onStop: () => (isGenerating.value = false)
        });
        const fetchActivitySummary = () => {
            pending.value = true;
            activitySummaryApi
                .fetchSummary(studyId, activityId)
                .then(applyResponse)
                .catch(applyError)
                .finally(() => (pending.value = false));
        };
        // Background refresh: no `pending` flip (no shimmer) and errors only log — never routed through
        // applyError, so a transient failure can't clobber the displayed summary.
        const refreshActivitySummary = () => {
            if (isGenerating.value)
                return;
            activitySummaryApi
                .fetchSummary(studyId, activityId)
                .then((res) => {
                // Guard against a late resolution after cleanup() — don't write refs on a disposed store.
                if (!disposed)
                    applyResponse(res);
            })
                .catch(console.error);
        };
        const generateActivitySummary = () => {
            // Clear the content so AiGenerationControls drops to its loading (shimmer) state. The
            // backend also clears its summary, so the poll resumes against an empty GET until the
            // new summary lands — matching task-summary.store.ts and ai-summary.store.ts.
            summaryContent.value = undefined;
            isSummaryOutdated.value = false;
            isGenerating.value = true;
            return activitySummaryApi
                .generateSummary(studyId, activityId)
                .then(() => polling.start())
                .catch((e) => {
                isGenerating.value = false;
                applyError(e);
                throw e;
            });
        };
        // Pinia store scope is the app, not the view. Components that mount this
        // store must call cleanup() in onUnmounted to stop any in-flight polling.
        const cleanup = () => {
            disposed = true;
            polling.stop();
        };
        return {
            pending,
            isGenerating,
            summaryContent,
            isSummaryOutdated,
            newMeetingCount,
            includedMeetingCount,
            totalMeetingCount,
            fetchActivitySummary,
            refreshActivitySummary,
            generateActivitySummary,
            cleanup
        };
    })();
};
